// src/components/CreateEventDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import { createEvent } from '../services/eventService';
import { CreateEventPayload } from '../types/church';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';

interface CreateEventDialogProps {
  open: boolean;
  onClose: () => void;
  egliseId: number;
  onSuccess: () => void;
}

const CreateEventDialog: React.FC<CreateEventDialogProps> = ({
  open,
  onClose,
  egliseId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useNotification();

  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState<'daily' | 'weekly' | 'monthly' | 'yearly' | null>(null);
  const [loading, setLoading] = useState(false);

  // Form validation errors
  const [errors, setErrors] = useState<{
    titre?: string;
    description?: string;
    date?: string;
  }>({});

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!titre.trim()) {
      newErrors.titre = t('validation.titleRequired');
    }

    if (!description.trim()) {
      newErrors.description = t('validation.descriptionRequired');
    }

    if (!date) {
      newErrors.date = t('validation.dateRequired');
    } else {
      const selectedDate = new Date(date);
      const now = new Date();
      if (selectedDate < now) {
        newErrors.date = t('validation.datePast');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const payload: CreateEventPayload = {
        titre: titre.trim(),
        description: description.trim(),
        date: new Date(date).toISOString(),
        eglise_id: egliseId,
        is_recurring: isRecurring,
        recurrence_pattern: isRecurring ? recurrencePattern : null,
      };

      await createEvent(payload);

      // Show success notification
      success(t('dialogs.createEvent.success'));

      // Reset form
      resetForm();

      // Callback
      onSuccess();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || t('dialogs.createEvent.error');
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitre('');
    setDescription('');
    setDate('');
    setIsRecurring(false);
    setRecurrencePattern(null);
    setErrors({});
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 1 }}>
                <EventIcon sx={{ color: '#2563eb', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {t('dialogs.createEvent.title')}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleClose} size="small" disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <TextField
            label={t('dialogs.createEvent.titleLabel')}
            value={titre}
            onChange={(e) => {
              setTitre(e.target.value);
              if (errors.titre) setErrors({ ...errors, titre: undefined });
            }}
            fullWidth
            required
            disabled={loading}
            error={!!errors.titre}
            helperText={errors.titre}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              },
            }}
          />

          <TextField
            label={t('dialogs.createEvent.descriptionLabel')}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (errors.description) setErrors({ ...errors, description: undefined });
            }}
            multiline
            rows={4}
            fullWidth
            required
            disabled={loading}
            error={!!errors.description}
            helperText={errors.description}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              },
            }}
          />

          <TextField
            label={t('dialogs.createEvent.dateTimeLabel')}
            type="datetime-local"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              if (errors.date) setErrors({ ...errors, date: undefined });
            }}
            fullWidth
            required
            disabled={loading}
            error={!!errors.date}
            helperText={errors.date}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              },
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                disabled={loading}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563eb' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2563eb' },
                }}
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                {t('dialogs.createEvent.recurringLabel')}
              </Typography>
            }
          />

          {isRecurring && (
            <FormControl fullWidth>
              <InputLabel>{t('dialogs.createEvent.recurrenceTypeLabel')}</InputLabel>
              <Select
                value={recurrencePattern || ''}
                onChange={(e) => setRecurrencePattern(e.target.value as any)}
                label={t('dialogs.createEvent.recurrenceTypeLabel')}
                disabled={loading}
                sx={{
                  borderRadius: 1.5,
                }}
              >
                <MenuItem value="daily">{t('admin.events.recurrence.daily')}</MenuItem>
                <MenuItem value="weekly">{t('admin.events.recurrence.weekly')}</MenuItem>
                <MenuItem value="monthly">{t('admin.events.recurrence.monthly')}</MenuItem>
                <MenuItem value="yearly">{t('admin.events.recurrence.yearly')}</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ borderRadius: 1.5 }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: 1.5,
            bgcolor: '#2563eb',
            '&:hover': { bgcolor: '#1d4ed8' },
            minWidth: 100,
          }}
        >
          {loading ? t('common.creating') : t('dialogs.createEvent.createButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEventDialog;
